import get from 'lodash.get';
import orderBy from 'lodash.orderby';

/**
 * Default class id needed on application initialization. If
 * classes property is not set in app properties or
 * classes is empty array - function will return empty string as default class id.
 * Otherwise function will return id of the first element.
 * @param classes {Object[]} array with classes data.
 * @returns {string} class id if classes list is not empty.
 */
export const getDefaultClassId = classes => get(classes, '[0].id', '');

/**
 * Returns class data by id.
 * @param classes {Object[]} array with classes data.
 * @param id {String} class id.
 * @returns {Object} class data f. e.:
 * {
 *     id: "1234567",
 *     gradeLevel: "02",
 *     name: "test class",
 * }
 */
export const getClassDataById = (classes, id) =>
    !Array.isArray(classes) ? undefined : classes.find(obj => get(obj, 'id') === id);

/**
 * Sort classes list by class name and isDemo flag. Demo classes should be
 * at the end of array. Class names should be sorted in alphabetical order.
 *
 * Example. Array with classes:
 * [
 *  {
 *      name: 'group2',
 *      isClass: false,
 *      isDemo: false,
 *  },
 *  {
 *      name: 'class1',
 *      isClass: true,
 *      isDemo: true,
 *  },
 *  {
 *      name: 'class4',
 *      isClass: true,
 *      isDemo: false,
 *  },
 *  {
 *      name: 'class3',
 *      isClass: true,
 *      isDemo: false,
 *  },
 * ]
 *
 * Will be sorted to array:
 * [
 *  {
 *      name: 'class3',
 *      isClass: true,
 *      isDemo: false,
 *  },
 *  {
 *      name: 'class4',
 *      isClass: true,
 *      isDemo: false,
 *  },
 *  {
 *      name: 'group2',
 *      isClass: false,
 *      isDemo: false,
 *  },
 *  {
 *      name: 'class1',
 *      isClass: true,
 *      isDemo: true,
 *  },
 * ]
 *
 * @param classes {Object[]} array with classes data.
 * @returns {Object[]} array with sorted classes date.
 */
export const sortClasses = classes =>
    !Array.isArray(classes)
        ? classes
        : orderBy(classes, ['isDemo', 'isClass', 'name'], ['asc', 'desc', 'asc']);

/**
 * Check if class is demo class.
 * @param classes {Object[]} array with classes data
 * @param classId {string} class id.
 *
 * @returns {boolean | undefined} boolean value whether class is demo class or undefined
 *                                if provided class id not found in classes array
 */
export const isDemoClass = (classes, classId) => get(getClassDataById(classes, classId), 'isDemo');
